import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { company } from '../data/content';
import { buildEstimateMessage, smsHrefFor } from '../utils/estimate';
import { MessageIcon, PhoneIcon, PlusIcon, AlertIcon, ShieldIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
const quickServices = ['Pool plaster', 'Remodel', 'Tile', 'Coping', 'Spray deck repair', 'Not sure'];
interface QuickValues {
  name: string;
  phone: string;
  zip: string;
  service: string;
}
const initialValues: QuickValues = {
  name: '',
  phone: '',
  zip: '',
  service: quickServices[0]
};
export function FloatingEstimate() {
  const [isOpen, setIsOpen] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [values, setValues] = useState<QuickValues>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 700) setShowTeaser(true);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const timer = window.setTimeout(() => setShowTeaser(true), 9000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const digits = values.phone.replace(/\D/g, '');
    if (!values.name.trim()) return setError('Please add your name.');
    if (digits.length < 10) return setError('Enter a 10-digit phone number.');
    if (!values.zip.trim()) return setError('City or ZIP, please.');
    setError(null);
    window.location.href = smsHrefFor(buildEstimateMessage({
      name: values.name,
      phone: values.phone,
      zip: values.zip,
      service: values.service
    }));
    setSent(true);
  };
  const teaserVisible = showTeaser && !teaserDismissed && !isOpen;
  return <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-end gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-id="element-194" data-mp="l5sypzf3n825">
      {/* Suggestion bubble */}
      <AnimatePresence data-id="element-195" data-mp="g4bv3w58d7hv">
        {teaserVisible && <motion.div initial={{
        opacity: 0,
        y: 14,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease
      }} className="pointer-events-auto w-full max-w-[19rem] rounded-lg border border-navy-100 bg-white p-4 shadow-2xl shadow-navy-900/25" data-id="element-196" data-mp="c4l7207395h1">
            <div className="flex items-start gap-3" data-id="element-197" data-mp="1wket8efdtwkx">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600" data-id="element-198" data-mp="1wiljgq9h2w8p">
                <MessageIcon className="h-4 w-4" data-id="element-199" data-mp="293af2evw77hq" />
              </span>
              <div className="min-w-0" data-id="element-200" data-mp="291h4wpm2m54z">
                <p className="font-display text-[15px] font-bold leading-snug text-navy-800" data-id="element-201" data-mp="2dth24aarajxv">
                  Still thinking about that pool?
                </p>
                <p className="mt-1 text-[13px] leading-snug text-navy-400" data-id="element-202" data-mp="2iv6crou2gvlf">
                  Get an honest number in 40 seconds — free, no pressure.
                </p>
              </div>
              <button type="button" onClick={() => setTeaserDismissed(true)} aria-label="Dismiss estimate suggestion" className="-mr-1 -mt-1 shrink-0 rounded-full p-1 text-navy-300 transition-colors duration-150 ease-out hover:text-navy-600" data-id="element-203" data-mp="1q4odkywgreda">
                <PlusIcon className="h-4 w-4 rotate-45" data-id="element-204" data-mp="3bhr5otq6xfr3" />
              </button>
            </div>
            <button type="button" onClick={() => {
          setIsOpen(true);
          setTeaserDismissed(true);
        }} className="mt-3 w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-205" data-mp="3bjlp23ltczt2">
              Get my free estimate
            </button>
          </motion.div>}
      </AnimatePresence>

      {/* Quick form panel */}
      <AnimatePresence data-id="element-206" data-mp="3rzvk3vq08ihv">
        {isOpen && <motion.div id="quick-estimate-panel" initial={{
        opacity: 0,
        y: 16,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease
      }} role="dialog" aria-label="Quick free estimate" className="pointer-events-auto w-full max-w-[22rem] overflow-hidden rounded-lg bg-white shadow-2xl shadow-navy-900/35" data-id="element-207" data-mp="3o16c6inqjt5m">
            <div className="flex items-start justify-between gap-4 bg-navy-800 px-5 py-4" data-id="element-208" data-mp="u6qq82ih5h29">
              <div data-id="element-209" data-mp="y8lo0x175cba">
                <p className="font-display text-base font-bold text-white" data-id="element-210" data-mp="y954saf41unq">Free estimate</p>
                <p className="mt-0.5 text-[13px] text-navy-200" data-id="element-211" data-mp="k9vrbiiaheng">
                  Three fields. We text you back the same day.
                </p>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Close estimate form" className="-mr-1 rounded-full p-1 text-navy-200 transition-colors duration-150 ease-out hover:text-white" data-id="element-212" data-mp="pa07f7fac7d5">
                <PlusIcon className="h-4 w-4 rotate-45" data-id="element-213" data-mp="1gi6w7c7cqm3" />
              </button>
            </div>

            {sent ? <div className="p-5" role="status" data-id="element-214" data-mp="3nq2w5scfxhxt">
                <p className="font-display text-base font-bold text-navy-800" data-id="element-215" data-mp="3nqclddpnvl2g">
                  Your text is ready — press send.
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-navy-400" data-id="element-216" data-mp="39k0xyy3nqucf">
                  We got your details queued to {company.phoneDisplay}. If your messaging app
                  didn&apos;t open, call us and we&apos;ll take it by phone.
                </p>
                <a href={company.phoneHref} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-sky-600 hover:text-sky-500" data-id="element-217" data-mp="2zsdd5g1lwbjx">
                  <PhoneIcon className="h-4 w-4" data-id="element-218" data-mp="3sh2dvdyr2ev5" />
                  Call {company.phoneDisplay}
                </a>
              </div> : <form onSubmit={handleSubmit} noValidate className="p-5" data-id="element-219" data-mp="3ik612w1tj6ks">
                <label htmlFor="quick-name" className="block text-[13px] font-semibold text-navy-700" data-id="element-220" data-mp="3dm4l4jzovndj">
                  Name
                </label>
                <input id="quick-name" type="text" autoComplete="name" value={values.name} onChange={e => setValues({
            ...values,
            name: e.target.value
          })} placeholder="Marisol Reyes" className="mt-1 w-full rounded-md border border-navy-200 px-3 py-2.5 text-[15px] text-navy-800 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-221" data-mp="277dfk2zmfed6" />

                <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-222" data-mp="wf5ma1pyam5l">
                  <div data-id="element-223" data-mp="11dfj0grbputb">
                    <label htmlFor="quick-phone" className="block text-[13px] font-semibold text-navy-700" data-id="element-224" data-mp="11bw2qq5o3jrj">
                      Phone
                    </label>
                    <input id="quick-phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={e => setValues({
                ...values,
                phone: e.target.value
              })} placeholder="(832) 555-0199" className="mt-1 w-full rounded-md border border-navy-200 px-3 py-2.5 text-[15px] text-navy-800 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-225" data-mp="1ab9b1fi222ao" />
                  </div>
                  <div data-id="element-226" data-mp="1wnu54gah45og">
                    <label htmlFor="quick-zip" className="block text-[13px] font-semibold text-navy-700" data-id="element-227" data-mp="1rmy5iex5n5ou">
                      City / ZIP
                    </label>
                    <input id="quick-zip" type="text" autoComplete="postal-code" value={values.zip} onChange={e => setValues({
                ...values,
                zip: e.target.value
              })} placeholder="Katy" className="mt-1 w-full rounded-md border border-navy-200 px-3 py-2.5 text-[15px] text-navy-800 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-228" data-mp="yz2aden8hlu6" />
                  </div>
                </div>

                <label htmlFor="quick-service" className="mt-3 block text-[13px] font-semibold text-navy-700" data-id="element-229" data-mp="2wuh7xxngyhsv">
                  What do you need?
                </label>
                <select id="quick-service" value={values.service} onChange={e => setValues({
            ...values,
            service: e.target.value
          })} className="mt-1 w-full rounded-md border border-navy-200 bg-white px-3 py-2.5 text-[15px] text-navy-800 focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-230" data-mp="2mwtdl8trzcb8">
                  {quickServices.map(service => <option key={service} data-id="element-231" data-mp="35sjcgn6evskw">{service}</option>)}
                </select>

                {error && <p role="alert" className="mt-3 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-232" data-mp="262kyjko8px4">
                    <AlertIcon className="h-3.5 w-3.5" data-id="element-233" data-mp="gc6kuvjwko6c" />
                    {error}
                  </p>}

                <button type="submit" className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-sky-400 px-4 py-3 font-display text-[15px] font-bold text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300" data-id="element-234" data-mp="larh6jw1anpi">
                  <MessageIcon className="h-4 w-4" data-id="element-235" data-mp="3tql015du8b9q" />
                  Text my request
                </button>
                <p className="mt-2.5 flex items-center justify-center gap-1.5 text-[12px] text-navy-400" data-id="element-236" data-mp="2dqcz1ab0rmm">
                  <ShieldIcon className="h-3.5 w-3.5 text-sky-500" data-id="element-237" data-mp="3juzvazhdwwwa" />
                  Licensed &amp; insured · we never share your info
                </p>
                <a href="#contact" onClick={() => setIsOpen(false)} className="mt-3 block text-center text-[13px] font-semibold text-sky-600 hover:text-sky-500" data-id="element-238" data-mp="3as8f6mdkdcyb">
                  Need the full form instead?
                </a>
              </form>}
          </motion.div>}
      </AnimatePresence>

      {/* Persistent trigger */}
      <button type="button" onClick={() => {
      setIsOpen(open => !open);
      setTeaserDismissed(true);
    }} aria-expanded={isOpen} aria-controls="quick-estimate-panel" className="pointer-events-auto flex items-center gap-2.5 rounded-full bg-navy-800 px-5 py-3.5 font-display text-[15px] font-bold text-white shadow-2xl shadow-navy-900/40 transition-[background-color,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-navy-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400" data-id="element-239" data-mp="357sq7aekyt5b">
        <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-240" data-mp="1qix70ehr1iq">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-300 opacity-75" data-id="element-241" data-mp="3sx3j65povj8w" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" data-id="element-242" data-mp="2qjxjfadv2i7n" />
        </span>
        {isOpen ? 'Close' : 'Free estimate'}
      </button>
    </div>;
}